import {
  CreatePhonemeInMediaHouseDocument,
  GetPhonemesFromMediaHouseDocument,
  PhonemeType,
} from "@/generated/graphql";
import { Button, FormField, Modal } from "@ai-teach/ui-web";
import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import toast from "react-hot-toast";
import { TargetIpaPicker } from "./TargetIpaPicker";

interface AddPhonemeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PHONEME_TYPE_OPTIONS: Array<{ value: PhonemeType; label: string }> = [
  { value: PhonemeType.Consonant, label: "Consonant" },
  { value: PhonemeType.ShortVowel, label: "Short Vowel" },
  { value: PhonemeType.LongVowel, label: "Long Vowel" },
  { value: PhonemeType.Digraph, label: "Digraph" },
  { value: PhonemeType.Trigraph, label: "Trigraph" },
  { value: PhonemeType.Blend, label: "Blend" },
  { value: PhonemeType.Diphthong, label: "Diphthong" },
  { value: PhonemeType.RControlled, label: "R-Controlled" },
  { value: PhonemeType.VowelTeam, label: "Vowel Team" },
  { value: PhonemeType.SilentLetter, label: "Silent Letter" },
  { value: PhonemeType.VariantVowel, label: "Variant Vowel" },
  { value: PhonemeType.Schwa, label: "Schwa" },
  { value: PhonemeType.SoftHard, label: "Soft/Hard" },
  { value: PhonemeType.SpecialPattern, label: "Special Pattern" },
  { value: PhonemeType.Other, label: "Other" },
];

/** phones of an Arpabet sequence, keyed so a repeated phone still gets a stable key */
function phoneOptions(sequence: string) {
  const seen = new Map<string, number>();
  return sequence
    .trim()
    .toLowerCase()
    .split("|")
    .map((phone, index) => {
      const occurrence = (seen.get(phone) ?? 0) + 1;
      seen.set(phone, occurrence);
      return { phone: phone || "\u2014", index, key: `${phone}-${occurrence}` };
    });
}

export function AddPhonemeModal({ isOpen, onClose }: AddPhonemeModalProps) {
  const [phoneme, setPhoneme] = useState("");
  const [phonemeType, setPhonemeType] = useState<PhonemeType>(PhonemeType.Consonant);
  const [azureText, setAzureText] = useState("");
  const [azureTargetIPA, setAzureTargetIPA] = useState<string[]>([]);
  const [hint, setHint] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [sequence, setSequence] = useState("");
  const [targetIndex, setTargetIndex] = useState(0);

  const [createPhoneme, { loading }] = useMutation(CreatePhonemeInMediaHouseDocument, {
    refetchQueries: [
      { query: GetPhonemesFromMediaHouseDocument, variables: { phonemeType: null } },
    ],
  });

  const handleSubmit = async () => {
    if (!phoneme.trim()) {
      toast.error("Phoneme text is required");
      return;
    }

    const trimmedSequence = sequence.trim().toLowerCase();
    if (!/^[a-z]{1,3}(\|[a-z]{1,3})*$/.test(trimmedSequence)) {
      toast.error("Arpabet sequence must be codes separated by | (e.g. b|ah)");
      return;
    }
    if (targetIndex >= trimmedSequence.split("|").length) {
      toast.error("Scored phone is outside the sequence");
      return;
    }

    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    try {
      await createPhoneme({
        variables: {
          input: {
            phoneme: phoneme.trim(),
            phonemeType,
            tags,
            arpabet: { sequence: trimmedSequence, targetIndex },
            ...(azureText.trim() && azureTargetIPA.length > 0
              ? { azureReference: { text: azureText.trim(), targetIPA: azureTargetIPA } }
              : {}),
            hint: hint.trim(),
          },
        },
      });
      toast.success("Phoneme created successfully");
      handleClose();
    } catch (error) {
      console.error("Failed to create phoneme:", error);
      toast.error("Failed to create phoneme. Please try again.");
    }
  };

  const handleClose = () => {
    setPhoneme("");
    setPhonemeType(PhonemeType.Consonant);
    setTagsInput("");
    setSequence("");
    setTargetIndex(0);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Phoneme" size="lg">
      <div className="space-y-4">
        <FormField
          id="phoneme"
          name="phoneme"
          label="Phoneme"
          type="text"
          value={phoneme}
          onChange={(e) => setPhoneme(e.target.value)}
          required
          placeholder="e.g. /sh/, /ch/, /ai/"
        />

        <div>
          <label htmlFor="phonemeType" className="block label mb-1">
            <span className="text-red-500">*</span> Type
          </label>
          <select
            id="phonemeType"
            value={phonemeType}
            onChange={(e) => setPhonemeType(e.target.value as PhonemeType)}
            className="mt-1 block w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {PHONEME_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="rounded-md border border-gray-800 p-3 space-y-3">
          <p className="text-xs text-secondary-400">
            Arpabet tells the pronunciation scorer what to listen for. Use a carrier vowel so the
            sound is audible — "b|ah" for /b/, "hh|ae" for short a — then pick which phone is
            scored.
          </p>
          <FormField
            id="arpabetSequence"
            name="arpabetSequence"
            label="Arpabet sequence"
            type="text"
            value={sequence}
            onChange={(e) => setSequence(e.target.value)}
            required
            placeholder="e.g. b|ah"
          />
          <div>
            <label htmlFor="arpabetTargetIndex" className="block label mb-1">
              <span className="text-red-500">*</span> Scored phone
            </label>
            <select
              id="arpabetTargetIndex"
              value={targetIndex}
              onChange={(e) => setTargetIndex(Number(e.target.value))}
              className="mt-1 block w-full px-3 py-2 bg-surface border border-gray-800 rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {phoneOptions(sequence).map((option) => (
                <option key={option.key} value={option.index}>
                  {option.index}: {option.phone}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="rounded-md border border-gray-800 p-3 space-y-3">
          <p className="text-xs text-secondary-400">
            Azure scores against spelling, not sounds. Give it a spelling it will pronounce as this
            phoneme — "buh" for b, "sss" for s, "hat" for short a — then choose the sound it should
            be scored on. Leave blank to fall back to the built-in table.
          </p>
          <FormField
            id="azureText"
            name="azureText"
            label="Azure reference spelling"
            type="text"
            value={azureText}
            onChange={(e) => setAzureText(e.target.value)}
            placeholder="e.g. buh"
          />
          <TargetIpaPicker value={azureTargetIPA} onChange={setAzureTargetIPA} idPrefix="add" />
        </div>

        <FormField
          id="hint"
          name="hint"
          label="Hint shown to the student"
          type="text"
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          placeholder="e.g. Curl your tongue back for the r sound"
        />

        <FormField
          id="tags"
          name="tags"
          label="Tags (comma-separated)"
          type="text"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          placeholder="e.g. digraph, consonant cluster"
        />

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? "Creating..." : "Create Phoneme"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
